// OAuth consent screen. claude.ai (or any OAuth MCP client) redirects the user
// here. We reuse the existing gathrxr.com Supabase session; on approval we mint
// a single-use authorization code (RLS insert) and redirect back with it. The
// token endpoint later exchanges that code (+ PKCE verifier) for an access token.

import React, { useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import TurnstileWidget from '../components/TurnstileWidget';
import { isEmailNotConfirmed, resendConfirmation } from '../services/onboarding';
import './DeveloperPage.css';

function randomCode(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
}

const OAuthAuthorizePage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get('client_id') || '';
  const redirectUri = params.get('redirect_uri') || '';
  const responseType = params.get('response_type') || '';
  const codeChallenge = params.get('code_challenge') || '';
  const codeChallengeMethod = params.get('code_challenge_method') || '';
  const state = params.get('state') || '';
  const scope = params.get('scope') || 'world:read world:write ai:generate';
  const resource = params.get('resource') || '';

  const [status, setStatus] = useState<'loading' | 'signin' | 'consent' | 'working' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  // Turnstile token for Supabase Auth bot protection (no-op until a site key is set).
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [clientName, setClientName] = useState<string>('An application');
  // Signed up but hasn't tapped the confirmation link yet.
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const [resendMsg, setResendMsg] = useState<string | null>(null);

  // Validate the request + client, then decide sign-in vs consent.
  useEffect(() => {
    (async () => {
      if (responseType !== 'code' || !clientId || !redirectUri || !codeChallenge) {
        setError('Invalid authorization request (missing response_type=code, client_id, redirect_uri, or PKCE challenge).');
        setStatus('error');
        return;
      }
      if (codeChallengeMethod && codeChallengeMethod !== 'S256') {
        setError('Unsupported PKCE method — only S256 is allowed.');
        setStatus('error');
        return;
      }
      // Validate redirect_uri against the registered client (prevents open redirect).
      const { data: client, error: cErr } = await supabase
        .from('oauth_clients').select('client_name,redirect_uris').eq('client_id', clientId).maybeSingle();
      if (cErr || !client) {
        setError('Unknown client_id. Register the client first.');
        setStatus('error');
        return;
      }
      if (Array.isArray(client.redirect_uris) && client.redirect_uris.length > 0 && !client.redirect_uris.includes(redirectUri)) {
        setError('redirect_uri does not match the registered client.');
        setStatus('error');
        return;
      }
      setClientName(client.client_name || 'An application');

      const { data } = await supabase.auth.getSession();
      if (data.session?.user) {
        setUserEmail(data.session.user.email || null);
        setStatus('consent');
      } else {
        setStatus('signin');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async () => {
    setError(null);
    setUnconfirmed(null);
    setResendMsg(null);
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password, options: { captchaToken: captcha || undefined } });
    setCaptchaReset((n) => n + 1);
    if (error && isEmailNotConfirmed(error)) { setUnconfirmed(email.trim()); return; }
    if (error) { setError(error.message); return; }
    setUserEmail(data.user?.email || null);
    setStatus('consent');
  };

  // The confirm link signs them in and brings them back to this same consent screen.
  const resend = async () => {
    if (!unconfirmed) return;
    const r = await resendConfirmation(unconfirmed, captcha, window.location.href);
    setResendMsg(r.message);
    setCaptchaReset((n) => n + 1);
  };

  const redirectBack = (extra: Record<string, string>) => {
    const url = new URL(redirectUri);
    for (const [k, v] of Object.entries(extra)) if (v) url.searchParams.set(k, v);
    if (state) url.searchParams.set('state', state);
    window.location.href = url.toString();
  };

  const approve = async () => {
    setStatus('working');
    setError(null);
    const { data: sess } = await supabase.auth.getSession();
    const uid = sess.session?.user?.id;
    if (!uid) { setStatus('signin'); return; }
    const code = randomCode();
    const { error } = await supabase.from('oauth_codes').insert({
      code, client_id: clientId, user_id: uid, redirect_uri: redirectUri,
      code_challenge: codeChallenge, scope, resource: resource || null,
      expires_at: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
    });
    if (error) { setError(error.message); setStatus('consent'); return; }
    redirectBack({ code });
  };

  const deny = () => redirectBack({ error: 'access_denied' });

  return (
    <div className="dev-page">
      <div className="dev-shell" style={{ maxWidth: 460 }}>
        <div className="dev-card">
          <h1 style={{ fontSize: 22 }}>Authorize access</h1>

          {status === 'loading' && <p className="dev-muted">Checking request…</p>}

          {status === 'error' && <div className="dev-error">{error}</div>}

          {status === 'signin' && (
            <>
              <p className="dev-muted"><strong>{clientName}</strong> wants to connect to your GathrXR account. Sign in to continue.</p>
              {error && <div className="dev-error">{error}</div>}
              {unconfirmed && (
                <div className="dev-error" role="alert">
                  Please confirm your email first. We sent a link to <strong>{unconfirmed}</strong> when you signed up; open it in this
                  browser and you'll come back here to finish connecting. Check your spam folder too.
                  <div style={{ marginTop: 8 }}>
                    <button className="dev-btn" onClick={resend}>Resend confirmation email</button>
                  </div>
                  {resendMsg && <div style={{ marginTop: 6 }}>{resendMsg}</div>}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
                <input className="dev-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <input className="dev-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && signIn()} />
                <TurnstileWidget action="signin" onToken={setCaptcha} resetKey={captchaReset} />
                <button className="dev-btn dev-btn-primary" onClick={signIn}>Sign in</button>
              </div>
            </>
          )}

          {(status === 'consent' || status === 'working') && (
            <>
              <p className="dev-muted">
                <strong>{clientName}</strong> is requesting permission to build and manage worlds in your GathrXR account
                {userEmail ? <> as <strong>{userEmail}</strong></> : null}.
              </p>
              <ul className="dev-muted dev-small" style={{ margin: '12px 0 18px', paddingLeft: 18, lineHeight: 1.7 }}>
                <li>Create, edit, and delete your worlds</li>
                <li>Place objects and generate assets (uses your AI quota)</li>
                <li>Scoped to exactly what your account can already do</li>
              </ul>
              {error && <div className="dev-error">{error}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="dev-btn dev-btn-primary" onClick={approve} disabled={status === 'working'}>{status === 'working' ? 'Authorizing…' : 'Authorize'}</button>
                <button className="dev-btn" onClick={deny} disabled={status === 'working'}>Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthAuthorizePage;
